import { Canvas as FiberCanvas, type RootState } from '@react-three/fiber';
import type Immutable from 'immutable';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { PCFSoftShadowMap } from 'three';
import ContactShadows from '../Shadows/ContactShadows';
import type { ShadowModel } from '../Shadows/types';
import CameraRig from '../Camera/Rig';
import type { CameraView } from '../Camera/types';
import ModelViewerProduct from '../ModelViewerProduct';
import { SHADOW_CACHE_VERSION } from '../Shadows/cache';
import TextureReady from './TextureReady';
import type { LabData } from '../../AppData/LabData';
import config from '../../../../config/config.json';
import RenderingContext from './RenderingContext';
import {
    DEFAULT_MODEL_VIEWER_PEDESTAL,
    getModelViewerPedestalLayout,
} from '../Presentation/Pedestal';
import {
    DEFAULT_MODEL_VIEWER_RENDER_SUPERSAMPLING,
    type ModelViewerRenderSupersampling,
} from '../Rendering/supersampling';
import {
    resolveModelViewerPreviewData,
    type ModelViewerPreviewDataInput,
} from '../previewData';
import Effects from '../PostEffects/Effects';
import { resolveModelViewerModelId } from '../ModelVariant';
import type { ModelViewerTextureUpdateSignal } from './TextureUpdateSignal';
import EmbeddedShadowMap from '../Shadows/EmbeddedShadowMap';
import { modelViewerPostEffectsAreActive } from '../PostEffects/config';
import type { ModelViewerMode } from '../ViewerMode';
import { hasFabricMaterialProfile } from '../fabricMaterialProfiles';
import {
    getCameraRelativeShadowLightRotationY,
    getCameraRelativeShadowViewKey,
} from '../Shadows/cameraRelative';

interface Props {
    productId: number
    labData: LabData
    options:Immutable.Map<string,string>
    variant: string
    cameraCommandId?: number
    modelRevision?: number
    cameraRevision?: number
    cameraEntryStartedAt?: number
    textureUpdateSignal: ModelViewerTextureUpdateSignal
    cameraPanEnabled?: boolean
    viewerMode: ModelViewerMode
    previewData?: ModelViewerPreviewDataInput
    renderSupersampling?: ModelViewerRenderSupersampling
    onControlsUsed?: () => void
    onCameraChange?: (view: CameraView) => void
    selectedModelVariant?: string
}

/**
 * R3F composition boundary. Stage reports centered bounds through context;
 * only then can the external cached-shadow receiver be sized and positioned.
 * Static rendering and Studio use that external path, while Neutral interactive
 * mode retains Stage's built-in shadows.
 */
const ViewerCanvas = memo((props:Props) => {
    // Resolve optional API data once at the scene boundary. Everything below
    // works with a complete preview, regardless of which caller mounted it.
    const previewData = useMemo(
        () => resolveModelViewerPreviewData(props.previewData),
        [props.previewData],
    )
    const {cyclorama, pedestal: pedestalData} = previewData.presentation
    const presentationSetup = previewData.presentation.setup
    const requestedPedestal = pedestalData.shape
    const pedestal = presentationSetup === 'studio'
        ? requestedPedestal
        : DEFAULT_MODEL_VIEWER_PEDESTAL
    const model = useMemo(() => resolveModelViewerModelId(
        props.labData,
        props.variant,
        previewData.model,
        props.selectedModelVariant,
    ), [
        previewData.model,
        props.labData,
        props.selectedModelVariant,
        props.variant,
    ])
    const onCreated = useCallback((state: RootState) => {
        if (config.ENVIRONMENT === 'prod') {
            state.gl.debug.checkShaderErrors = false
        }
    }, [])
    const staticRendering = props.viewerMode === 'render'
    const websiteMode = props.viewerMode === 'website'
    const renderSupersampling = props.renderSupersampling
        ?? DEFAULT_MODEL_VIEWER_RENDER_SUPERSAMPLING
    const shadowViewKey = staticRendering
        ? getCameraRelativeShadowViewKey(previewData.camera)
        : undefined
    const shadowLightRotationY = staticRendering
        ? getCameraRelativeShadowLightRotationY(previewData.camera)
        : undefined
    // Include every geometry input that can alter the footprint. Changes to
    // the bake algorithm are represented by SHADOW_CACHE_VERSION. Renderbot
    // shadows also include their camera view because the bake rotates its
    // light rig into that camera's frame.
    const shadowCacheKey = useMemo(() => JSON.stringify([
        SHADOW_CACHE_VERSION,
        props.productId,
        props.variant,
        model,
        props.options.entrySeq().toArray().sort(([leftKey], [rightKey]) => (
            leftKey.localeCompare(rightKey)
        )),
        shadowViewKey,
    ]), [
        props.options,
        props.productId,
        props.variant,
        model,
        shadowViewKey,
    ])
    const [centeredModel, setCenteredModel] = useState<ShadowModel>()
    // Stage may report equivalent bounds on several demand frames. Preserve
    // object identity when nothing relevant changed to avoid restarting the
    // shadow/pedestal pipeline.
    const reportCenteredModel = useCallback((model: ShadowModel) => {
        setCenteredModel(currentModel => (
            currentModel?.cacheKey === model.cacheKey
            && currentModel.depth === model.depth
            && currentModel.modelRevision === model.modelRevision
            && currentModel.radius === model.radius
            && currentModel.height === model.height
            && currentModel.width === model.width
                ? currentModel
                : model
        ))
    }, [])
    const externalShadowRendering = staticRendering || presentationSetup === 'studio'
    const fabricLighting = (
        props.productId === 13 || props.productId === 144
    ) && hasFabricMaterialProfile(
        props.options.get('fabric') ?? (props.productId === 144 ? 'matte-crepe' : undefined),
    )
    const postEffectsActive = (
        props.cameraRevision !== undefined
        || modelViewerPostEffectsAreActive(previewData.post_effects)
    )
    const renderingContext = useMemo(() => ({
        fabricLighting,
        modelRevision: props.modelRevision,
        presentationSetup,
        reportCenteredModel: externalShadowRendering
            ? reportCenteredModel
            : undefined,
        shadowCacheKey,
        viewerMode: props.viewerMode,
    }), [
        externalShadowRendering,
        fabricLighting,
        props.modelRevision,
        props.viewerMode,
        presentationSetup,
        reportCenteredModel,
        shadowCacheKey,
    ])
    const activeCenteredModel = centeredModel?.cacheKey === shadowCacheKey
        ? centeredModel
        : undefined
    // Pedestal dimensions and the compensating camera distance are derived
    // only after Stage has centered and measured the selected GLB.
    const pedestalLayout = useMemo(() => (
        activeCenteredModel
            ? getModelViewerPedestalLayout(activeCenteredModel, pedestal)
            : undefined
    ), [activeCenteredModel, pedestal])
    // modelRevision synchronizes a render request; it is intentionally not
    // part of cache identity because the geometry may already be baked.
    const activeExternalShadowModel = useMemo(() => (
        externalShadowRendering && activeCenteredModel
            ? {
                ...activeCenteredModel,
                modelRevision: props.modelRevision,
            }
            : undefined
    ), [
        activeCenteredModel,
        externalShadowRendering,
        props.modelRevision,
    ])

    return <FiberCanvas
        // Interactive DPR remains adaptive. Static DPR is the requested
        // supersampling factor and PreviewImageExporter downsamples the final
        // capture.
        dpr={staticRendering ? renderSupersampling : [1, 2]}
        gl={{ preserveDrawingBuffer: staticRendering }}
        frameloop="demand"
        shadows={{ type: PCFSoftShadowMap }}
        onCreated={onCreated}
        //Necessary or else it breaks the lab currently
        style={{pointerEvents: staticRendering ? 'none' : 'inherit'}}
    >
        {/* Embedded viewers inherit the Lab/dialog surface. The standalone
            workspace and renderbot retain the output background so authoring
            continues to match the exported preview. */}
        {!websiteMode
            ? <color
                attach="background"
                args={[presentationSetup === 'studio' ? cyclorama.color : '#f3f3f3']}
            />
            : null}
        <RenderingContext.Provider value={renderingContext}>
            <CameraRig
                labData={props.labData}
                model={model}
                variant={props.variant}
                previewData={previewData}
                cameraCommandId={props.cameraCommandId}
                cameraPanEnabled={props.cameraPanEnabled}
                cameraRevision={props.cameraRevision}
                entryAnimationStartedAt={props.cameraEntryStartedAt}
                cameraDistanceScale={pedestalLayout?.cameraDistanceScale}
                deferDefaultCamera={pedestal !== 'none' && pedestalLayout === undefined}
                onControlsUsed={props.onControlsUsed}
                onCameraChange={props.onCameraChange}
            />
            <TextureReady signal={props.textureUpdateSignal} />
            <Suspense fallback={null}>
                <ModelViewerProduct
                    // Keep the scene controls mounted while a newly selected
                    // model loads so its camera can reset without replaying
                    // the website entry animation.
                    // Static geometry is remounted on cache identity changes
                    // so Stage cannot retain measurements from a prior request.
                    key={staticRendering ? shadowCacheKey : undefined}
                    productId={props.productId}
                    options={props.options}
                    variant={props.variant}
                    model={model}
                />
            </Suspense>
            {websiteMode
                ? <EmbeddedShadowMap geometryKey={shadowCacheKey} />
                : null}
            {externalShadowRendering
                ? <ContactShadows
                    cycloramaColor={presentationSetup === 'studio' ? cyclorama.color : undefined}
                    cycloramaGlossiness={cyclorama.glossiness}
                    fadeEdges={presentationSetup === 'studio'}
                    lightRotationY={shadowLightRotationY}
                    model={activeExternalShadowModel}
                    pedestal={pedestal}
                    pedestalMaterial={pedestalData.material}
                    progressive={!staticRendering}
                    reflectionRevision={props.cameraRevision}
                    transmissionRevision={props.cameraRevision}
                />
                : null}
            {postEffectsActive
                ? <Effects
                    effects={previewData.post_effects}
                    revision={props.cameraRevision}
                />
                : null}
        </RenderingContext.Provider>
    </FiberCanvas>
})

export default ViewerCanvas
